import { useMemo } from 'react';
import { BsLightningChargeFill } from 'react-icons/bs';
import { MdLocalShipping, MdPlace } from 'react-icons/md';

import { twMerge } from 'tailwind-merge';
import { CONSIGNMENT_STATUS } from 'themes/common';

import {
  FontSize,
  FontWeight,
  STATUSES,
  deliveryModes,
  salesMode,
} from 'commons';
import { Heading, Text, Timer, useDrawer } from 'components';
import { formatTimestamp } from 'helpers';
import { Consignment as ConsignmentType } from 'types';

interface Props {
  action?: () => void;
  isSelected: boolean;
  consignment: ConsignmentType;
}

const superExpressFlagHeight = '22px';

export function DashboardConsignment({
  action,
  isSelected,
  consignment,
}: Props) {
  const { setIsOpen } = useDrawer();

  const { AVAILABLE_FOR_STORE, RESERVED_BY_STORE, MANUALLY_DELIVERED } =
    STATUSES;
  const {
    availableStores,
    consignmentCode,
    consignmentStatus: status,
    endTimer,
    customerName,
    createdDate,
    deliveryModeId,
    isSuperExpress,
  } = consignment;

  const distance = availableStores[0]?.distance || 0;

  const { hour, minute, day, month } = formatTimestamp(createdDate);

  const isShippingFromStore = consignment.salesMode === salesMode.shipping;
  const salesModeText = isShippingFromStore
    ? 'Entrega pela loja'
    : 'Retire na loja';

  const salesModeIcon = isShippingFromStore ? (
    <MdLocalShipping className="h-full w-full opacity-50" />
  ) : (
    <MdPlace className="h-full w-full opacity-50" />
  );

  const showTimerStatuses = [
    AVAILABLE_FOR_STORE.value,
    RESERVED_BY_STORE.value,
  ];
  const showTimer = showTimerStatuses.includes(status);

  const deliveryMode = deliveryModes.find(
    deliveryMode => deliveryModeId === deliveryMode.value
  );
  const isManuallyDelivered = status === MANUALLY_DELIVERED.value;
  const collector = isManuallyDelivered
    ? 'Cliente retirou'
    : deliveryMode?.label;

  const hasSuperExpressFlag = useMemo(
    () => isSuperExpress && isShippingFromStore,
    [isSuperExpress, isShippingFromStore]
  );

  function handleAction() {
    setIsOpen(true);
    if (!action) return;
    action();
  }

  const baseClassName =
    'relative flex min-w-unset animate-fade-in cursor-pointer flex-col justify-between overflow-hidden rounded-2xl bg-neutral-100 opacity-100';

  const shadowClassName =
    (isSelected && 'shadow-consignment-card shadow-neutral-700') || '';

  const buttonClassName = `${baseClassName} ${shadowClassName}`;

  return (
    <button
      className={buttonClassName}
      style={{
        minHeight: hasSuperExpressFlag
          ? `calc(130px + ${superExpressFlagHeight})`
          : '132px',
        padding: hasSuperExpressFlag
          ? `calc(15px + ${superExpressFlagHeight}) 15px 15px`
          : '15px',
      }}
      onClick={handleAction}
    >
      {hasSuperExpressFlag && (
        <div
          className={twMerge(
            `w-full h-[${superExpressFlagHeight}] absolute left-0 top-0 flex
            items-center gap-1 bg-danger-500 px-2 py-1 text-neutral-100`
          )}
        >
          <BsLightningChargeFill />
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            Super Expresso
          </Text>
        </div>
      )}
      <div
        className="flex w-full items-start justify-between gap-1 text-font12
          text-neutral-500"
      >
        <div className="flex flex-col items-start gap-1">
          <Heading as="h3" size={FontSize.font14} weight={FontWeight.bold}>
            {consignmentCode}
          </Heading>

          <p className="max-w-32 truncate mobile:max-w-48">{customerName}</p>
        </div>

        <div
          className="flex flex-col items-end gap-1 text-font10
            mobile:text-font12"
        >
          <div className="flex flex-wrap items-center justify-end gap-1">
            <div className="flex h-4 w-4 items-center justify-center">
              {salesModeIcon}
            </div>

            <p>{salesModeText}</p>

            {isShippingFromStore && (
              <p>
                / <strong>{Math.round(distance)}km</strong>
              </p>
            )}
          </div>

          <p className="max-w-32 truncate mobile:max-w-48">
            {isShippingFromStore && collector}
          </p>
        </div>
      </div>

      <hr className="h-px w-full bg-neutral-700 opacity-10" />

      <div
        className="flex w-full items-end justify-between gap-1 text-font12
          text-neutral-500"
      >
        <div className="flex flex-col items-start gap-2">
          <p>
            Pedido feito em {day}/{month} ás {hour}h{minute}
          </p>

          <div
            className="flex items-center justify-center rounded-[50px] px-4
              py-1"
            style={{
              backgroundColor: `${CONSIGNMENT_STATUS.consignmentStatus[status]?.primary}`,
              color: `${CONSIGNMENT_STATUS.consignmentStatus[status]?.secondary}`,
            }}
          >
            {STATUSES[status]?.label || 'Status desconhecido'}
          </div>
        </div>

        {showTimer && (
          <Timer
            className={twMerge(hasSuperExpressFlag && 'text-danger-500')}
            endTimer={endTimer}
          />
        )}
      </div>
    </button>
  );
}
